import { useState } from 'react';
import { Plus, ChevronRight, Folder, Edit2, Trash2, X, Check } from 'lucide-react';
import { useFavorites } from '@/context/FavoritesContext';
import type { FavoriteList } from '@/types/favorites';

interface FavoritesListManagerProps {
  selectedListId: string | null;
  onSelectList: (listId: string | null) => void;
}

export function FavoritesListManager({ selectedListId, onSelectList }: FavoritesListManagerProps) {
  const { lists, favorites, createList, updateList, deleteList } = useFavorites();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  // Count favorites in each list
  const getListCount = (listId: string) => {
    return favorites.filter((fav) => fav.listIds.includes(listId)).length;
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) return;

    try {
      await createList(newListName.trim());
      setNewListName('');
      setIsCreating(false);
    } catch (error) {
      console.error('Failed to create list:', error);
    }
  };

  const handleStartEdit = (list: FavoriteList) => {
    setEditingListId(list.id);
    setEditingName(list.name);
  };

  const handleSaveEdit = async () => {
    if (!editingListId || !editingName.trim()) return;

    try {
      await updateList(editingListId, editingName.trim());
      setEditingListId(null);
      setEditingName('');
    } catch (error) {
      console.error('Failed to update list:', error);
    }
  };

  const handleDelete = async (listId: string) => {
    if (
      !confirm('Are you sure you want to delete this list? Items will remain in your favorites.')
    ) {
      return;
    }

    try {
      await deleteList(listId);
      if (selectedListId === listId) {
        onSelectList(null);
      }
    } catch (error) {
      console.error('Failed to delete list:', error);
    }
  };

  return (
    <div className="border-b border-slate-100">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Folder size={16} className="text-[#8e4585]" />
          <span className="text-sm font-medium text-slate-700">My Lists</span>
          {lists.length > 0 && (
            <span className="text-xs bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full">
              {lists.length}
            </span>
          )}
        </div>
        <ChevronRight
          size={16}
          className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
        />
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-3 space-y-2">
          {/* All Favorites Button */}
          <button
            onClick={() => onSelectList(null)}
            className={`
              w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors
              ${
                selectedListId === null
                  ? 'bg-[#f8e8f8] text-[#8e4585] font-medium'
                  : 'text-slate-600 hover:bg-slate-100'
              }
            `}
          >
            <span>All Favorites</span>
            <span className="ml-auto text-xs opacity-70">{favorites.length}</span>
          </button>

          {/* Custom Lists */}
          {lists.map((list) => (
            <div key={list.id} className="flex items-center gap-1">
              {editingListId === list.id ? (
                // Edit mode
                <div className="flex-1 flex items-center gap-1">
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit();
                      if (e.key === 'Escape') setEditingListId(null);
                    }}
                    className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#8e4585]"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveEdit}
                    className="p-1 text-green-600 hover:bg-green-50 rounded"
                  >
                    <Check size={14} />
                  </button>
                  <button
                    onClick={() => setEditingListId(null)}
                    className="p-1 text-slate-400 hover:bg-slate-100 rounded"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                // View mode
                <>
                  <button
                    onClick={() => onSelectList(list.id)}
                    className={`
                      flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left
                      ${
                        selectedListId === list.id
                          ? 'bg-[#f8e8f8] text-[#8e4585] font-medium'
                          : 'text-slate-600 hover:bg-slate-100'
                      }
                    `}
                  >
                    <span className="truncate">{list.name}</span>
                    <span className="ml-auto text-xs opacity-70">{getListCount(list.id)}</span>
                  </button>
                  <button
                    onClick={() => handleStartEdit(list)}
                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                    title="Rename list"
                  >
                    <Edit2 size={12} />
                  </button>
                  <button
                    onClick={() => handleDelete(list.id)}
                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                    title="Delete list"
                  >
                    <Trash2 size={12} />
                  </button>
                </>
              )}
            </div>
          ))}

          {/* Create New List */}
          {isCreating ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateList();
                  if (e.key === 'Escape') setIsCreating(false);
                }}
                placeholder="List name..."
                className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#8e4585]"
                autoFocus
              />
              <button
                onClick={handleCreateList}
                disabled={!newListName.trim()}
                className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
              >
                <Check size={14} />
              </button>
              <button
                onClick={() => {
                  setIsCreating(false);
                  setNewListName('');
                }}
                className="p-1 text-slate-400 hover:bg-slate-100 rounded"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsCreating(true)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#8e4585] hover:bg-[#f8e8f8] rounded-lg transition-colors"
            >
              <Plus size={14} />
              <span>Create new list</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
